// @ts-strict-ignore
import React, { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAtom, useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useCreateTransferMutation } from 'hooks/mutations/useCreateTransferMutation'
import { GetTransferPayloadProps, MovePayload, TransferStep } from 'pages/NestedCustomers/types'
import { useWalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { BaseErrorResponse } from 'types/beneficiaries'
import { TransferRequest } from 'types/unifiedPayouts/unifiedPayouts'
import { logSentryError } from 'utils/sentry'
import { apiErrorsAtom, formDataAtom, transferErrorAtom } from './atoms/atoms'
import { useSourceWallet } from './hooks/useSourceWallet'
import { MoveComplete } from './MoveComplete'
import { MoveConfirm } from './MoveConfirm'
import { MoveForm } from './MoveForm'

export const Move = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [transferStep, setTransferStep] = useState<TransferStep>(TransferStep.FORM)
  const [formData, setFormData] = useAtom(formDataAtom)
  const [walletAction, setWalletAction] = useWalletActionContext()
  const setTransferError = useSetAtom(transferErrorAtom)
  const setApiErrors = useSetAtom(apiErrorsAtom)
  const sourceWallet = useSourceWallet()
  const { onClose: handleClose } = walletAction || {}

  const { mutate: createTransferMutation, isPending: isLoadingCreateTransfer } =
    useCreateTransferMutation()

  const handleFormContinue = (data: MovePayload) => {
    setFormData(data)
    setTransferStep(TransferStep.CONFIRMATION)
  }

  const handleOnBack = () => {
    setTransferStep(TransferStep.FORM)
    setWalletAction({
      ...walletAction,
      header: t('customers.moveFunds'),
      onBack: null,
    })
  }

  const getTransferPayload = ({
    destinationWalletId,
    transferAmount,
    paymentReference,
  }: GetTransferPayloadProps): TransferRequest => {
    return {
      walletId: sourceWallet?.walletId,
      amount: transferAmount,
      currency: sourceWallet?.currency,
      reference: paymentReference,
      beneficiary: {
        walletId: destinationWalletId,
      },
    }
  }

  const handleSubmit = async () => {
    setTransferError(null)
    setApiErrors([])

    const payload = getTransferPayload({
      destinationWalletId: formData?.destinationWallet,
      transferAmount: formData?.amount,
      paymentReference: formData?.paymentReference,
    })

    createTransferMutation(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['transferWallets'] })
        setWalletAction({
          ...walletAction,
          header: null,
          onBack: null,
        })
        setTransferStep(TransferStep.COMPLETE)
      },
      onError: (error: BaseErrorResponse) => {
        const errorList = error?.errorList
        if (errorList?.length) {
          setApiErrors(errorList)
          setTransferStep(TransferStep.FORM)
        } else {
          setTransferError(t('customers.transferError'))
        }
        logSentryError('useCreateTransferMutation', error, payload)
      },
    })
  }

  return (
    <>
      {transferStep === TransferStep?.FORM && (
        <MoveForm
          onContinue={handleFormContinue}
          onCancel={handleClose}
          isSubmitting={isLoadingCreateTransfer}
        />
      )}

      {transferStep === TransferStep?.CONFIRMATION && (
        <MoveConfirm
          isLoading={isLoadingCreateTransfer}
          handleSubmit={handleSubmit}
          onBack={handleOnBack}
        />
      )}

      {transferStep === TransferStep?.COMPLETE && <MoveComplete />}
    </>
  )
}
